import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, distinctUntilChanged, map } from 'rxjs';
import { environment } from '../../../environments/environment';
import { BrowserStorage } from '../../models/browserStorage';
import {
	ChapterProgress,
	ProgressSignal,
	applyProgressSignals,
	isSignalSatisfied,
	mergeSignals,
	signalFromChapter,
} from '../../utils/course-progress.utils';

export interface ProgressSyncContext {
	userId: string;
	categoryEnrollmentId: string;
	courseEnrollmentId: string;
}

export interface ProgressSyncState {
	// Progress made on this device that the server is not yet known to hold.
	pending: number;
	// The last attempt to persist it failed; cleared as soon as a write lands.
	failing: boolean;
}

const STORAGE_PREFIX = 'progress-outbox:v1:';
const SERVER_SEND_TIMEOUT_MS = 10_000;
const IDLE: ProgressSyncState = { pending: 0, failing: false };

/**
 * Keeps the learner's progress on the device until the server has it.
 *
 * Progress lives in one array inside the course enrollment document, so every save is a Firestore
 * transaction, and a transaction needs a live round trip: on a weak mobile link it times out and the
 * completion only existed in memory. Reloading then read the server copy and sent the learner back to
 * re-watch the video. Everything recorded here is re-applied to every copy of the enrollment the app
 * reads, and dropped only once a written document is seen to contain it.
 */
@Injectable({
	providedIn: 'root',
})
export class ProgressSyncService {
	private readonly states = new BehaviorSubject<{ [key: string]: ProgressSyncState }>({});
	private readonly memory = new Map<string, { [chapterId: string]: ProgressSignal }>();

	state$(context: ProgressSyncContext): Observable<ProgressSyncState> {
		const key = this.key(context);
		return this.states.pipe(
			map(states => states[key] ?? this.initialState(key)),
			distinctUntilChanged((a, b) => a.pending === b.pending && a.failing === b.failing),
		);
	}

	hasPending(context: ProgressSyncContext): boolean {
		return Object.keys(this.read(this.key(context))).length > 0;
	}

	// Snapshots the forward progress of every chapter about to be written.
	record(context: ProgressSyncContext, chapters: ChapterProgress[] | null | undefined): void {
		if (!this.isComplete(context) || !Array.isArray(chapters)) {
			return;
		}
		const key = this.key(context);
		const stored = this.read(key);
		let changed = false;
		for (const chapter of chapters) {
			const signal = signalFromChapter(chapter);
			if (!signal) {
				continue;
			}
			const merged = mergeSignals(stored[signal.chapterId], signal);
			if (JSON.stringify(merged) !== JSON.stringify(stored[signal.chapterId])) {
				stored[signal.chapterId] = merged;
				changed = true;
			}
		}
		if (changed) {
			this.write(key, stored);
		}
	}

	// Re-applies progress the server does not have yet onto chapters just read from it.
	apply(context: ProgressSyncContext, chapters: ChapterProgress[] | null | undefined): boolean {
		if (!this.isComplete(context)) {
			return false;
		}
		const signals = Object.values(this.read(this.key(context)));
		return applyProgressSignals(chapters, signals);
	}

	// Called with the chapters a successful write left on the server; drops what they now hold.
	acknowledge(context: ProgressSyncContext, persistedChapters: any[] | null | undefined): void {
		if (!this.isComplete(context)) {
			return;
		}
		const key = this.key(context);
		const stored = this.read(key);
		const byId = new Map((persistedChapters ?? []).filter(chapter => chapter?.id).map(chapter => [chapter.id, chapter]));
		for (const [chapterId, signal] of Object.entries(stored)) {
			const persisted = byId.get(chapterId);
			// A chapter the enrollment no longer has can never be satisfied; keeping it would pin the
			// "not saved" warning on forever.
			if (!persisted || isSignalSatisfied(persisted, signal)) {
				delete stored[chapterId];
			}
		}
		this.write(key, stored, false);
	}

	/**
	 * Sends the progress still on the device to the server's progress endpoint: one small HTTPS
	 * request instead of a Firestore transaction's round trips, and with `keepalive` it survives the
	 * page closing. Forward-only and idempotent on the server, so resending is harmless.
	 *
	 * Plain fetch rather than ApiService on purpose: the HTTP interceptor signs the learner out on a
	 * 401, and an expired session must only leave the progress here until they sign in again.
	 */
	async sendToServer(context: ProgressSyncContext, options: { keepalive?: boolean } = {}): Promise<boolean> {
		if (!this.isComplete(context)) {
			return false;
		}
		const signals = Object.values(this.read(this.key(context)));
		if (!signals.length) {
			return true;
		}
		const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
		const timer = controller && !options.keepalive ? setTimeout(() => controller.abort(), SERVER_SEND_TIMEOUT_MS) : null;
		try {
			const response = await fetch(`${environment.API_URL}/courseEnrollment/progress`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json',
					'Authorization': BrowserStorage.get('userToken'),
				},
				body: JSON.stringify({
					categoryEnrollmentId: context.categoryEnrollmentId,
					courseEnrollmentId: context.courseEnrollmentId,
					signals,
				}),
				keepalive: !!options.keepalive,
				signal: controller?.signal,
			});
			if (!response.ok) {
				return false;
			}
			// The server holds it now. The local copy stays until a Firestore read or write confirms
			// it (acknowledge), but there is nothing left to warn the learner about.
			const key = this.key(context);
			this.publish(key, { pending: Object.keys(this.read(key)).length, failing: false });
			return true;
		} catch {
			return false;
		} finally {
			if (timer) {
				clearTimeout(timer);
			}
		}
	}

	markFailed(context: ProgressSyncContext): void {
		if (!this.isComplete(context)) {
			return;
		}
		const key = this.key(context);
		const pending = Object.keys(this.read(key)).length;
		this.publish(key, { pending, failing: pending > 0 });
	}

	private isComplete(context: ProgressSyncContext): boolean {
		return !!(context?.userId && context?.categoryEnrollmentId && context?.courseEnrollmentId);
	}

	private key(context: ProgressSyncContext): string {
		return `${STORAGE_PREFIX}${context?.userId}:${context?.categoryEnrollmentId}:${context?.courseEnrollmentId}`;
	}

	private initialState(key: string): ProgressSyncState {
		const pending = Object.keys(this.read(key)).length;
		return pending ? { pending, failing: false } : IDLE;
	}

	private read(key: string): { [chapterId: string]: ProgressSignal } {
		try {
			const raw = localStorage.getItem(key);
			if (raw) {
				const parsed = JSON.parse(raw);
				if (parsed && typeof parsed === 'object') {
					this.memory.set(key, parsed);
					return { ...parsed };
				}
			}
		} catch {
			// Storage can be unavailable (private mode, blocked site data); the in-memory copy still
			// covers this page's lifetime.
		}
		return { ...(this.memory.get(key) ?? {}) };
	}

	private write(key: string, signals: { [chapterId: string]: ProgressSignal }, keepFailing = true): void {
		this.memory.set(key, signals);
		try {
			if (Object.keys(signals).length) {
				localStorage.setItem(key, JSON.stringify(signals));
			} else {
				localStorage.removeItem(key);
			}
		} catch {
			// See read().
		}
		const pending = Object.keys(signals).length;
		const failing = keepFailing && pending > 0 && !!this.states.value[key]?.failing;
		this.publish(key, { pending, failing });
	}

	private publish(key: string, state: ProgressSyncState): void {
		this.states.next({ ...this.states.value, [key]: state });
	}
}
